/* MASONTEX · Unified Request page controller
   Sole owner of product selection + direct sample action on /pages/order-a-sample. */
(() => {
  'use strict';
  const STORAGE_KEY = 'masontexSampleRequestV2';

  const readSaved = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (_) { return {}; }
  };
  const writeSaved = (payload) => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (_) {}
  };
  const clean = (value = '') => String(value || '').trim();
  const fmt = (template = '', values = {}) => String(template).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => values[key] ?? '');

  const initSampleRequest = (root) => {
    if (!root || root.dataset.msSampleReady === '1') return;
    root.dataset.msSampleReady = '1';

    let i18n = {};
    try { i18n = JSON.parse(root.querySelector('[data-ms-sample-i18n]')?.textContent || '{}'); }
    catch (_) {}

    const params = new URLSearchParams(window.location.search);
    const intent = clean(params.get('intent')) || 'sample';
    const saved = readSaved();
    const restoreRequested = params.get('restore') === '1';

    const picker = root.querySelector('[data-ms-picker]');
    const productOptions = [...root.querySelectorAll('[data-ms-product-option]')];
    const openButtons = [...root.querySelectorAll('[data-ms-open-picker]')];
    const closeButton = root.querySelector('[data-ms-close-picker]');
    const searchInput = root.querySelector('[data-ms-picker-search]');
    const filterButtons = [...root.querySelectorAll('[data-ms-filter]')];
    const noResults = root.querySelector('[data-ms-no-results]');
    const form = root.querySelector('#MsStandaloneSampleRequestForm');
    const quantityInput = form?.querySelector('[data-ms-unified-quantity], [data-ms-quantity]');
    const marketInput = form?.querySelector('[data-ms-unified-market]');
    const selectionError = root.querySelector('[data-ms-selection-error]');
    const live = root.querySelector('[data-ms-selection-live]');

    const fields = {
      company: form?.querySelector('[data-ms-unified-company]'),
      name: form?.querySelector('[data-ms-unified-name]'),
      market: marketInput,
      email: form?.querySelector('[data-ms-unified-email]'),
      phone: form?.querySelector('[data-ms-unified-phone]'),
      notes: form?.querySelector('[data-ms-unified-notes]')
    };

    const stripModel = root.querySelector('[data-ms-strip-model]');
    const stripVersion = root.querySelector('[data-ms-strip-version]');
    const stripColor = root.querySelector('[data-ms-strip-color]');
    const stripQty = root.querySelector('[data-ms-strip-qty]');
    const mobileModel = root.querySelector('[data-ms-mobile-model]');

    const summaryModel = root.querySelector('[data-ms-summary-model]');
    const summaryVersion = root.querySelector('[data-ms-summary-version]');
    const summaryImage = root.querySelector('[data-ms-summary-image]');
    const summaryEmpty = root.querySelector('[data-ms-summary-empty]');
    const summaryVersionCell = root.querySelector('[data-ms-summary-version-cell]');
    const summaryColor = root.querySelector('[data-ms-summary-color]');
    const summaryBag = root.querySelector('[data-ms-summary-bag]');
    const summaryBox = root.querySelector('[data-ms-summary-box]');
    const summaryMoq = root.querySelector('[data-ms-summary-moq]');
    const summaryQty = root.querySelector('[data-ms-summary-qty]');

    const submitButton = form?.querySelector('button[type="submit"]');
    const paymentLink = form?.querySelector('[data-ms-payment-link], [data-ms-unified-sample]');
    const sampleNote = form?.querySelector('[data-ms-unified-sample-note]');
    const hidden = (key) => root.querySelector(`[data-ms-hidden="${key}"]`);

    let selected = null;
    let activeFilter = 'all';
    let lastTrigger = null;

    const normalizeQuantity = () => {
      const parsed = Number.parseInt(quantityInput?.value || '1', 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    };
    const normalizeMoq = (value) => {
      const match = String(value || '100').match(/\d[\d,]*/);
      return match ? match[0].replace(/,/g, '') : '100';
    };
    const fieldValue = (field) => clean(field?.value);

    const setPaymentLabel = (text) => {
      const label = paymentLink?.querySelector('span');
      if (label) label.textContent = text;
    };

    const syncQuantity = () => {
      const value = `${normalizeQuantity()} PCS`;
      if (stripQty) stripQty.textContent = value;
      if (summaryQty) summaryQty.textContent = value;
    };

    const buildPaymentTarget = () => {
      if (!selected?.sampleReady || !selected.sampleUrl) return '';
      const target = new URL(selected.sampleUrl, window.location.origin);
      target.searchParams.set('qty', '1');
      target.searchParams.set('source', 'form');
      target.searchParams.set('intent', intent);
      target.searchParams.set('reference', 'FORM SAMPLE');
      const market = fieldValue(fields.market);
      if (market) target.searchParams.set('market', market);
      return `${target.pathname}${target.search}`;
    };

    const syncPaymentAction = () => {
      if (!paymentLink) return;
      paymentLink.hidden = false;

      if (!selected) {
        paymentLink.href = '#';
        paymentLink.dataset.msCheckoutEnabled = 'false';
        paymentLink.setAttribute('aria-disabled', 'false');
        setPaymentLabel(i18n.buySample || 'BUY SAMPLE');
        if (sampleNote) sampleNote.textContent = i18n.chooseFirstNote || 'Choose a helmet first. Direct sample checkout appears when that model has an active sample SKU.';
        return;
      }

      if (!selected.sampleReady || !selected.sampleUrl) {
        paymentLink.href = '#';
        paymentLink.dataset.msCheckoutEnabled = 'false';
        paymentLink.setAttribute('aria-disabled', 'true');
        setPaymentLabel(i18n.samplePriceToConfirm || 'SAMPLE PRICE TO CONFIRM');
        if (sampleNote) sampleNote.textContent = i18n.notConnectedNote || 'Direct sample checkout is not connected for this model yet. Send the wholesale request and our export team will confirm sample availability and sample pricing.';
        return;
      }

      paymentLink.href = buildPaymentTarget();
      paymentLink.dataset.msCheckoutEnabled = 'true';
      paymentLink.setAttribute('aria-disabled', 'false');
      setPaymentLabel(`${fmt(i18n.buyModelSample || 'BUY {{ model }} SAMPLE', { model: selected.model })}${selected.samplePrice ? ` · ${selected.samplePrice}` : ''}`);
      if (sampleNote) sampleNote.textContent = i18n.skipNote || 'Skip quotation and proceed directly to the secure sample-payment page. Your form details are preserved without submitting the wholesale request.';
    };

    const saveForSamplePayment = () => {
      if (!selected?.sampleReady) return {};
      const previous = readSaved();
      const payload = {
        ...previous,
        source: 'form',
        originSource: clean(params.get('source')) || 'request-page',
        intent,
        handle: selected.handle,
        model: selected.model,
        product: selected.title,
        productUrl: selected.url,
        quantity: 1,
        requestQuantity: normalizeQuantity(),
        market: fieldValue(fields.market),
        country: fieldValue(fields.market),
        company: fieldValue(fields.company),
        name: fieldValue(fields.name),
        email: fieldValue(fields.email),
        phone: fieldValue(fields.phone),
        whatsapp: fieldValue(fields.phone),
        notes: fieldValue(fields.notes),
        requirements: fieldValue(fields.notes),
        reference: 'FORM SAMPLE',
        checkoutProduct: selected.sampleCheckoutProduct,
        savedAt: Date.now()
      };
      writeSaved(payload);
      return payload;
    };

    const classify = (keywords = '') => {
      const value = keywords.toLowerCase();
      if (value.includes('flip up') || value.includes('flip-up') || value.includes('modular')) return 'flip-up';
      if (value.includes('full face') || value.includes('full-face')) return 'full-face';
      if (value.includes('open face') || value.includes('open-face') || value.includes('3/4')) return 'open-face';
      if (value.includes('half helmet') || value.includes('half-helmet') || value.includes('half face')) return 'half';
      return 'other';
    };

    const resetPickerView = () => {
      if (searchInput) searchInput.value = '';
      activeFilter = 'all';
      filterButtons.forEach((button) => {
        const active = button.dataset.msFilter === 'all';
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      productOptions.forEach((option) => { option.hidden = false; });
      if (noResults) noResults.hidden = true;
    };

    const applyPickerFilters = () => {
      const query = clean(searchInput?.value).toLowerCase();
      let visible = 0;
      productOptions.forEach((option) => {
        const keywords = option.dataset.productKeywords || '';
        const category = classify(keywords);
        const matchesFilter = activeFilter === 'all' || category === activeFilter;
        const matchesSearch = !query || keywords.includes(query);
        option.hidden = !(matchesFilter && matchesSearch);
        if (!option.hidden) visible += 1;
      });
      if (noResults) noResults.hidden = visible !== 0;
    };

    const openPicker = (trigger) => {
      if (!picker) return;
      lastTrigger = trigger || document.activeElement;
      resetPickerView();
      if (typeof picker.showModal === 'function') {
        if (!picker.open) picker.showModal();
      } else picker.setAttribute('open', '');
      document.documentElement.classList.add('ms-sample-picker-open');
      window.setTimeout(() => searchInput?.focus(), 30);
    };

    const closePicker = () => {
      if (!picker) return;
      if (typeof picker.close === 'function' && picker.open) picker.close();
      else picker.removeAttribute('open');
    };

    const selectProduct = (option) => {
      if (!option) return;
      const data = option.dataset;
      selected = {
        title: data.productTitle || data.productModel || 'Selected helmet',
        model: data.productModel || data.productTitle || 'Selected helmet',
        type: data.productType || 'Motorcycle Helmet',
        url: data.productUrl || '',
        handle: data.productHandle || '',
        image: data.productImage || '',
        moq: normalizeMoq(data.productMoq || '100'),
        sampleReady: data.productSampleReady === 'true',
        sampleUrl: data.productSampleUrl || '',
        samplePrice: data.productSamplePrice || '',
        sampleCheckoutProduct: data.productSampleCheckoutProduct || ''
      };

      productOptions.forEach((item) => {
        const active = item === option;
        item.classList.toggle('is-selected', active);
        item.setAttribute('aria-pressed', String(active));
      });

      root.classList.add('has-selected-product');
      if (stripModel) stripModel.textContent = selected.model;
      if (stripVersion) stripVersion.textContent = i18n.standard || 'STANDARD';
      if (stripColor) stripColor.textContent = i18n.toConfirm || 'TO CONFIRM';
      if (mobileModel) mobileModel.textContent = selected.model;
      if (summaryModel) summaryModel.textContent = selected.model;
      if (summaryVersion) summaryVersion.textContent = i18n.standardVersion || 'STANDARD VERSION';
      if (summaryVersionCell) summaryVersionCell.textContent = i18n.standardVersion || 'STANDARD VERSION';
      if (summaryColor) summaryColor.textContent = i18n.toConfirm || 'TO CONFIRM';
      if (summaryBag) summaryBag.textContent = i18n.bagIncluded || 'PROTECTIVE HELMET BAG · INCLUDED';
      if (summaryBox) summaryBox.textContent = i18n.boxIncluded || 'INDIVIDUAL INNER BOX · INCLUDED';
      if (summaryMoq) summaryMoq.textContent = `${selected.moq} PCS`;

      if (summaryImage) {
        if (selected.image) {
          summaryImage.src = selected.image;
          summaryImage.alt = selected.title;
          summaryImage.hidden = false;
        } else {
          summaryImage.hidden = true;
          summaryImage.removeAttribute('src');
        }
      }
      if (summaryEmpty) summaryEmpty.hidden = Boolean(selected.image);

      const values = {
        product: selected.title,
        url: selected.url,
        model: selected.model,
        type: selected.type,
        handle: selected.handle,
        version: 'Standard Version',
        color: 'To confirm',
        moq: `${selected.moq} PCS`,
        packaging: 'Protective Helmet Bag + Individual Inner Box'
      };
      Object.entries(values).forEach(([key, fieldValue]) => {
        const input = hidden(key);
        if (input) {
          input.value = fieldValue;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      openButtons.forEach((button) => { button.textContent = i18n.changeHelmet || 'CHANGE HELMET'; });
      const emptyButton = summaryEmpty?.querySelector('button');
      if (emptyButton) emptyButton.textContent = i18n.changeHelmetArrow || 'CHANGE HELMET →';
      if (selectionError) selectionError.hidden = true;
      if (live) live.textContent = fmt(selected.sampleReady ? (i18n.selectedReady || '{{ model }} selected. Direct sample checkout is available.') : (i18n.selectedPending || '{{ model }} selected. Sample checkout requires confirmation.'), { model: selected.model });
      syncQuantity();
      syncPaymentAction();
      closePicker();
    };

    const restoreFormState = () => {
      if (!restoreRequested || !saved || typeof saved !== 'object') return;
      const restored = {
        company: saved.company,
        name: saved.name,
        market: saved.market || saved.country,
        email: saved.email,
        phone: saved.phone || saved.whatsapp,
        notes: saved.notes || saved.requirements
      };
      Object.entries(restored).forEach(([key, value]) => {
        if (fields[key] && value != null) fields[key].value = String(value);
      });
      if (quantityInput && saved.requestQuantity) quantityInput.value = String(saved.requestQuantity);
    };

    openButtons.forEach((button) => button.addEventListener('click', () => openPicker(button)));
    closeButton?.addEventListener('click', closePicker);
    picker?.addEventListener('close', () => {
      document.documentElement.classList.remove('ms-sample-picker-open');
      if (lastTrigger instanceof HTMLElement) lastTrigger.focus({ preventScroll: true });
    });
    picker?.addEventListener('click', (event) => { if (event.target === picker) closePicker(); });
    productOptions.forEach((option) => option.addEventListener('click', () => selectProduct(option)));
    searchInput?.addEventListener('input', applyPickerFilters);
    filterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        activeFilter = button.dataset.msFilter || 'all';
        filterButtons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        applyPickerFilters();
      });
    });

    const handleRequestDetailChange = () => {
      syncQuantity();
      syncPaymentAction();
    };
    quantityInput?.addEventListener('input', handleRequestDetailChange);
    quantityInput?.addEventListener('change', handleRequestDetailChange);
    marketInput?.addEventListener('input', syncPaymentAction);
    marketInput?.addEventListener('change', syncPaymentAction);

    paymentLink?.addEventListener('click', (event) => {
      event.preventDefault();
      if (!selected) {
        openPicker(paymentLink);
        return;
      }
      if (!selected.sampleReady || !selected.sampleUrl) {
        if (live) live.textContent = fmt(i18n.directNotConnected || 'Direct sample checkout is not connected for {{ model }}. Send the wholesale request to confirm sample availability.', { model: selected.model });
        return;
      }
      const target = buildPaymentTarget();
      if (!target) return;
      saveForSamplePayment();
      window.location.assign(target);
    });

    form?.addEventListener('submit', (event) => {
      if (selected) return;
      event.preventDefault();
      if (selectionError) selectionError.hidden = false;
      openPicker(submitButton);
    });

    restoreFormState();
    syncQuantity();
    syncPaymentAction();

    const requestedHandle = clean(params.get('product')) || (restoreRequested ? clean(saved.handle) : '');
    if (requestedHandle) {
      const requested = productOptions.find((option) => option.dataset.productHandle === requestedHandle);
      if (requested) selectProduct(requested);
    }
  };

  const boot = (scope = document) => scope.querySelectorAll('[data-ms-sample-request]').forEach(initSampleRequest);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
